import { useEffect } from 'react';
import { setPendingTraining } from '../../Redux/store/userSlice';
import { useDispatch } from 'react-redux';

const ModuleList = ({ modules = [], onSelect, summary }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (summary?.required_modules > 0 && summary?.pending_modules == 0) {
      dispatch(setPendingTraining(false));
    }
  }, [summary]);
  if (!modules.length) {
    return (
      <div className="flex flex-col bg-white rounded-md shadow-md items-center justify-center py-16 text-center">
        <div className="text-4xl mb-3">📄</div>
        <h3 className="text-lg font-semibold text-gray-700">
          No Modules Found
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          No training content is available{' '}
        </p>
      </div>
    );
  }

  return (
    <div className="lg:min-w-[78vw] lg:max-w-[80vw]">
      {summary?.required_modules > 0 && summary?.pending_modules > 0 && (
        <div className="mb-6">
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl shadow-sm">
            {/* Icon */}
            <span className="material-symbols-outlined text-amber-600 mt-[2px]">
              warning
            </span>

            {/* Content */}
            <div className="flex-1">
              <p className="text-sm font-semibold">Training Required</p>
              <p className="text-xs mt-1 text-amber-700">
                You have pending required training modules. Please complete them
                to unlock full access to the platform.
              </p>
            </div>

            {/* Optional CTA */}
            <button
              onClick={() => {
                const el = document.getElementById('training-modules');
                el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-xs font-medium bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1.5 rounded-lg transition"
            >
              View Modules
            </button>
          </div>
        </div>
      )}
      <div className="w-full p-4 bg-white rounded-lg shadow-lg">
        <div className="flex w-full  flex-col md:flex-row justify-between items-start md:items-center pb-5 border-gray-100">
          <div className="flex items-center mb-2 md:mb-0">
            <span
              className={`material-symbols-outlined mr-2 text-xl transition-all duration-300 text-black`}
            >
              school{' '}
            </span>
            <span className="font-medium lg:text-xl text-sm">
              Training & Learning
            </span>
          </div>
        </div>
        {/* Training Summary Dashboard */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Modules */}
          <div className="bg-white border rounded-2xl p-4 shadow-sm hover:shadow-md transition">
            <p className="text-xs text-gray-500">Total Modules</p>
            <p className="text-xl font-semibold text-gray-900 mt-1">
              {summary?.total_modules || 0}
            </p>
          </div>

          {/* Completed */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
            <p className="text-xs text-emerald-600">Completed</p>
            <p className="text-xl font-semibold text-emerald-700 mt-1">
              {summary?.completed_modules || 0}
            </p>
          </div>

          {/* Pending */}
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
            <p className="text-xs text-amber-600">Pending</p>
            <p className="text-xl font-semibold text-amber-700 mt-1">
              {summary?.pending_modules || 0}
            </p>
          </div>

          {/* Required */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4">
            <p className="text-xs text-indigo-600">Required</p>
            <p className="text-xl font-semibold text-indigo-700 mt-1">
              {summary?.required_modules || 0}
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((mod) => (
            <div
              key={mod.id}
              onClick={() => onSelect(mod)}
              className="group relative bg-white border border-gray-100 rounded-2xl p-6 cursor-pointer 
                 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              {/* Glow accent */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition duration-300 
                      bg-gradient-to-br from-indigo-50 via-transparent to-transparent pointer-events-none"
              />

              {/* Header */}
              <div className="flex justify-between items-center mb-4 relative z-10">
                <span className="material-symbols-outlined mr-2 text-xl transition-all duration-300 text-black">
                  menu_book
                </span>

                <div className="flex items-center gap-2">
                  {/* ✅ Required Badge */}
                  {mod.is_required && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-red-50 text-red-600 tracking-wide">
                      REQUIRED
                    </span>
                  )}

                  {!mod.is_required && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-500 tracking-wide">
                      OPTIONAL
                    </span>
                  )}

                  {/* ✅ Existing Status Badge */}
                  <span
                    className={`text-xs font-semibold px-3 py-1 rounded-full tracking-wide
        ${
          mod.completion_status === 'completed'
            ? 'bg-emerald-50 text-emerald-600'
            : mod.completion_status === 'pending'
              ? 'bg-amber-50 text-amber-600'
              : 'bg-gray-100 text-gray-600'
        }`}
                  >
                    {mod.completion_status
                      ? mod.completion_status.toUpperCase()
                      : 'DRAFT'}
                  </span>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-base font-semibold text-gray-900 leading-snug line-clamp-2 relative z-10">
                {mod.title}
              </h3>

              {/* Description */}
              {mod.description && (
                <p className="text-sm text-gray-500 mt-2 line-clamp-2 relative z-10">
                  {mod.description}
                </p>
              )}

              {/* Footer */}
              <div className="flex justify-between items-center mt-5 text-sm relative z-10">
                <span className="text-gray-500">
                  <span className="font-semibold text-gray-700">
                    {mod.total_questions || 0}
                  </span>{' '}
                  Questions
                </span>

                <span
                  className="flex items-center gap-1 text-[#375AE7] font-medium 
                         group-hover:gap-2 transition-all duration-200"
                >
                  View Details
                  <span className="text-lg">→</span>
                </span>
              </div>

              {/* Bottom Accent Line */}
              <div
                className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r 
                      from-indigo-500 via-purple-500 to-pink-500 
                      opacity-0 group-hover:opacity-100 transition rounded-b-2xl"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ModuleList;
